import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ClipboardPaste, Copy, Crosshair, RotateCcw, Target } from 'lucide-react'
import HomeShell from './HomeShell'
import CalculatorMap from '@/components/calculator/CalculatorMap'
import { useLanguage } from '@/contexts/LanguageContext'
import { fetchMaps, type MapInfo } from '@/lib/maps'
import { mapFilter, useMapViewMode } from '@/lib/mapView'
import {
  DEFAULT_WEAPON_ID, WEAPONS, elevationSolutions, findWeapon, formatMil,
} from '@/lib/artillery'
import {
  azimuthDeg, boundsCenter, clampToBounds, distanceMeters, formatGameCoord,
  parsePointText, pointText, type GameCalibration, type GamePoint,
} from '@/lib/gameMap'

/**
 * turksletloose.com/calculator — Wardogs havan / obüs hesap makinesi.
 *
 * Balistik tablolar ve harita kalibrasyonu açık kaynak Wardogs Artillery
 * Calculator'dan alındı (MIT, github.com/apollyon-sys/wardogs-calculator);
 * arayüz, harita zemini ve koordinat sistemi bizim. Harita, strateji
 * editörünün kullandığı tile piramidinin aynısıdır — yani üçüncü taraf bir
 * gömme değil, kendi sunucumuzdan servis edilen kendi haritamız.
 *
 * Hesap tamamen tarayıcıda yapılır; backend'den yalnızca harita kataloğu
 * istenir (`/api/home/maps`, oturumsuz). Sayfa hiçbir şey kaydetmez: batarya ve
 * hedef yalnızca tarayıcının kendi `localStorage`'ında durur, böylece sekme
 * kapanıp açılınca batarya yerinde kalır.
 */

type Which = 'battery' | 'target'

const STORAGE_KEY = 'tll_calculator'

interface Stored {
  map?: string
  weapon?: string
  /** Harita başına batarya + hedef: kullanıcı haritalar arasında gezerken
   *  her haritanın kendi kurulumu bozulmasın. */
  points?: Record<string, { battery: GamePoint; target: GamePoint }>
}

function readStored(): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Stored) : {}
  } catch {
    return {}   // gizli sekme, bozuk değer: hesap makinesi yine çalışır
  }
}

/** Haritanın varsayılan kurulumu: batarya ortada, hedef bir ızgara kare kuzeyde. */
function defaultPoints(cal: GameCalibration) {
  const battery = boundsCenter(cal)
  return {
    battery,
    target: clampToBounds({ x: battery.x, y: battery.y + 3 }, cal),
  }
}

/**
 * Sayı girdisi. Yazarken geçersiz ara durumlar (`-`, `12.`) kaybolmasın diye
 * taslak metin yerelde tutulur; geçerli bir sayıya dönüştüğü anda yukarı
 * bildirilir, odak çıkınca değer biçimine geri oturur.
 */
function CoordInput({
  label, value, onChange,
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const shown = draft ?? formatGameCoord(value)

  return (
    <label className="flex min-w-0 flex-1 items-center gap-1.5">
      <span className="font-mono text-[11px] text-muted-foreground">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        /* `size` şart: varsayılan içsel genişlik ~20 karakterdir ve satırın
           min-content'ini 400 px'e çıkarır — telefon genişliğinde sağ sütun
           bu yüzden taşıyordu. Genişliği `flex-1` veriyor, `size` yalnızca
           tabanı düşürüyor. */
        size={7}
        value={shown}
        onChange={e => {
          setDraft(e.target.value)
          const parsed = Number(e.target.value.replace(',', '.'))
          if (Number.isFinite(parsed)) onChange(parsed)
        }}
        onBlur={() => setDraft(null)}
        className="min-w-0 flex-1 rounded-sm border border-white/12 bg-black/40 px-2 py-1 font-mono text-[12px] text-foreground outline-none transition-colors focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-ring"
      />
    </label>
  )
}

export default function MortarCalculator() {
  const { T } = useLanguage()
  const [viewMode, setViewMode] = useMapViewMode()

  const [maps, setMaps] = useState<MapInfo[] | null>(null)
  const [failed, setFailed] = useState(false)

  const stored = useRef<Stored>(readStored())
  const [mapName, setMapName] = useState<string>(stored.current.map ?? '')
  const [weaponId, setWeaponId] = useState<string>(stored.current.weapon ?? DEFAULT_WEAPON_ID)
  const [place, setPlace] = useState<Which>('target')
  const [points, setPoints] = useState<{ battery: GamePoint; target: GamePoint } | null>(null)

  useEffect(() => {
    fetchMaps('public')
      // Kalibrasyonsuz harita atış çözümü veremez; uç nokta da onları
      // göndermez, ama liste burada da süzülür ki `cal` asla undefined olmasın.
      .then(rows => setMaps(rows.filter(m => m.game)))
      .catch(() => setFailed(true))
  }, [])

  const map = useMemo(() => {
    if (!maps?.length) return null
    return maps.find(m => m.name === mapName) ?? maps[0]
  }, [maps, mapName])

  const cal = map?.game ?? null
  const weapon = findWeapon(weaponId)

  // Harita hazır olduğunda noktalar kurulur: kaydedilmiş kurulum varsa o,
  // yoksa haritanın ortası.
  useEffect(() => {
    if (!map || !cal) return
    const saved = stored.current.points?.[map.name]
    setPoints(
      saved
        ? { battery: clampToBounds(saved.battery, cal), target: clampToBounds(saved.target, cal) }
        : defaultPoints(cal),
    )
    setMapName(map.name)
  }, [map, cal])

  // Seçimler ve noktalar tarayıcıda kalır — batarya kurulumu sekme kapanınca
  // kaybolmasın. Yazma gecikmeli: sürükleme kare başına yeni bir nokta üretir,
  // her karede JSON serileştirip diske yazmanın hiçbir karşılığı yok. Yazma
  // başarısız olursa (gizli sekme) sessizce geçilir.
  useEffect(() => {
    if (!map || !points) return
    const next: Stored = {
      ...stored.current,
      map: map.name,
      weapon: weaponId,
      points: { ...stored.current.points, [map.name]: points },
    }
    stored.current = next
    const timer = window.setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch { /* gizli sekme */ }
    }, 400)
    return () => window.clearTimeout(timer)
  }, [map, weaponId, points])

  const move = useCallback((which: Which, p: GamePoint) => {
    setPoints(current => (current ? { ...current, [which]: p } : current))
  }, [])

  const setCoord = (which: Which, axis: 'x' | 'y', v: number) => {
    if (!cal || !points) return
    move(which, clampToBounds({ ...points[which], [axis]: v }, cal))
  }

  const reset = () => {
    if (cal) setPoints(defaultPoints(cal))
  }

  // ── Kopyala / yapıştır ────────────────────────────────────────────────────
  //
  // Biçim wardogs-artillery.com'un kopyaladığıyla aynı (`x72.45, y31.20`), yani
  // iki araç arasında koordinat elle yeniden yazılmaz.
  const [flash, setFlash] = useState<{ which: Which; text: string } | null>(null)
  const flashTimer = useRef<number | null>(null)
  const say = (which: Which, text: string) => {
    setFlash({ which, text })
    if (flashTimer.current) window.clearTimeout(flashTimer.current)
    flashTimer.current = window.setTimeout(() => setFlash(null), 1400)
  }
  useEffect(() => () => { if (flashTimer.current) window.clearTimeout(flashTimer.current) }, [])

  const copy = async (which: Which) => {
    if (!points) return
    const text = pointText(points[which])
    try {
      await navigator.clipboard.writeText(text)
      say(which, T.calculator.copied)
    } catch {
      // Pano izni yoksa metni gösteren bir istem hiç olmazsa elle kopyalanabilir.
      window.prompt(T.calculator.copyPrompt, text)
    }
  }

  const paste = async (which: Which) => {
    if (!cal) return
    let text: string | null = null
    try {
      text = await navigator.clipboard.readText()
    } catch {
      text = window.prompt(T.calculator.pastePrompt, '')
    }
    if (text === null) return
    const parsed = parsePointText(text)
    if (!parsed) {
      say(which, T.calculator.invalid)
      return
    }
    move(which, clampToBounds(parsed, cal))
    say(which, T.calculator.pasted)
  }

  // ── Çözüm ─────────────────────────────────────────────────────────────────
  const solution = useMemo(() => {
    if (!cal || !points) return null
    const distance = distanceMeters(points.battery, points.target, cal)
    return {
      distance,
      azimuth: azimuthDeg(points.battery, points.target),
      east: (points.target.x - points.battery.x) * cal.metersPerUnit,
      north: (points.target.y - points.battery.y) * cal.metersPerUnit,
      ...elevationSolutions(weapon, distance),
    }
  }, [cal, points, weapon])

  /**
   * Yükseliş satırı: havan tek değer verir, obüs aynı mesafeye iki yay —
   * düz ve dik. Menzil içinde olduğu hâlde tabloda karşılığı yoksa (obüsün
   * 780–1181 m aralığında yalnızca dik yay var) yalnızca olan yazılır.
   */
  const elevation = useMemo(() => {
    if (!solution) return null
    if (!solution.inRange) return { rows: [], note: T.calculator.outOfRangeNote }
    const rows: { label: string | null; value: string }[] = []
    const single = formatMil(solution.single)
    const low = formatMil(solution.low)
    const high = formatMil(solution.high)
    if (single) rows.push({ label: null, value: single })
    if (low) rows.push({ label: T.calculator.lowArc, value: low })
    if (high) rows.push({ label: T.calculator.highArc, value: high })
    return { rows, note: rows.length ? null : T.calculator.noSolution }
  }, [solution, T])

  /**
   * Zemin karartması.
   *
   * Uydu görüntüsü öğle güneşinde çekilmiş gibi parlak: yeşil tarla ile kırmızı
   * menzil çizgisinin parlaklığı birbirine yakın, dolayısıyla asıl bilgi —
   * çemberler, hat, işaretçiler — zeminle yarışıyordu. Karartma, haritayı
   * *bilgi taşıyan bir zemin* hâline getirir; zemin söner, üstündekiler parlar.
   *
   * Taktiksel modda daha az karartılır: o mod zaten parlaklığı ortak bir
   * ortalamaya (`lib/mapView.ts`) çekiyor, üstüne aynı miktarı bindirmek
   * haritayı okunmaz siyaha indirirdi.
   *
   * ⚠️ Uydu modundaki pay da küçüldü (0.72 → 0.88): `mapFilter` artık uydu
   * görüntüsünü kendisi yumuşatıyor (`brightness(0.82) saturate(0.45)`), yani
   * toplam karartma yine ölçülen 0.72 civarında kalıyor — iki karartma üst üste
   * binseydi harita siyaha inerdi. Doygunluk payı da düştü, taban zaten kırptı.
   *
   * Menzil dışındaki alanı ayrıca söndüren maske haritanın kendisinde
   * (`CalculatorMap`); buradaki karartma tüm zemine, oradaki yalnızca
   * vurulamayan yere uygulanır ve ikisi üst üste biner.
   */
  const groundFilter = [
    mapFilter(map?.luma, viewMode),
    viewMode === 'tactical' ? 'brightness(0.9)' : 'brightness(0.88)',
  ]
    .filter(Boolean)
    .join(' ')

  const selectClass =
    'w-full cursor-pointer appearance-none rounded-sm border border-white/12 bg-black/40 px-2.5 py-1.5 ' +
    'font-display text-[12px] font-semibold text-foreground outline-none transition-colors ' +
    'hover:bg-black/60 focus-visible:ring-2 focus-visible:ring-ring'

  const iconBtnClass =
    'flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border border-white/12 bg-white/5 ' +
    'text-foreground/70 transition-colors hover:bg-white/10 hover:text-foreground ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  /** Bir noktanın satırı: yerleştirme düğmesi, iki koordinat, kopyala/yapıştır. */
  const pointRow = (which: Which) => {
    if (!points) return null
    const active = place === which
    const Icon = which === 'battery' ? Crosshair : Target
    return (
      <div
        className={`rounded-sm border p-3 transition-colors ${
          active ? 'border-primary/50 bg-primary/5' : 'border-white/10 bg-black/20'
        }`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setPlace(which)}
            aria-pressed={active}
            className={`flex items-center gap-1.5 rounded-sm px-1.5 py-1 font-display text-[11px] font-bold uppercase tracking-wider transition-colors ${
              active ? 'text-primary' : 'text-foreground/60 hover:text-foreground'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {which === 'battery' ? T.calculator.battery : T.calculator.target}
          </button>

          <span className="ml-auto text-[10px] text-muted-foreground">
            {flash?.which === which ? flash.text : active ? T.calculator.placing : ''}
          </span>

          <button
            type="button"
            onClick={() => void copy(which)}
            title={T.calculator.copy}
            aria-label={T.calculator.copy}
            className={iconBtnClass}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => void paste(which)}
            title={T.calculator.paste}
            aria-label={T.calculator.paste}
            className={iconBtnClass}
          >
            <ClipboardPaste className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="mt-2.5 flex items-center gap-2">
          <CoordInput label="x" value={points[which].x} onChange={v => setCoord(which, 'x', v)} />
          <CoordInput label="y" value={points[which].y} onChange={v => setCoord(which, 'y', v)} />
        </div>
      </div>
    )
  }

  const stat = (label: string, value: string, tone?: 'good' | 'bad') => (
    <div className="rounded-sm border border-white/10 bg-black/20 px-3 py-2">
      <p className="font-display text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p
        className={`mt-1 font-mono text-[15px] font-semibold ${
          tone === 'good' ? 'text-[#82c596]' : tone === 'bad' ? 'text-[#d86666]' : 'text-foreground'
        }`}
      >
        {value}
      </p>
    </div>
  )

  return (
    <HomeShell pageTitle={T.calculator.pageTitle}>
      <section className="mx-auto w-full max-w-6xl px-5 pb-14 pt-8 md:pt-10">
        <h1 className="font-display text-3xl font-extrabold uppercase leading-[0.98] tracking-tight md:text-5xl">
          {T.calculator.title}
        </h1>
        <p className="mt-4 flex items-center gap-3 font-display text-[11px] font-bold uppercase tracking-[0.3em] text-foreground/60 md:text-[13px]">
          <span className="h-px w-8 shrink-0 bg-primary" />
          {T.calculator.kicker}
        </p>
        <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-foreground/80">
          {T.calculator.lead}
        </p>

        {failed && (
          <p className="mt-8 rounded-sm border border-[#d86666]/40 bg-[#d86666]/10 px-4 py-3 text-[13px] text-foreground/85">
            {T.calculator.mapsUnavailable}
          </p>
        )}

        {!failed && (
          <div className="mt-8 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)]">
            {/* ── Harita ────────────────────────────────────────────────── */}
            <div className="relative h-[58vh] min-h-[380px] overflow-hidden rounded-md border border-white/12 lg:h-[calc(100vh-14rem)] lg:min-h-[520px]">
              {map && cal && points ? (
                <CalculatorMap
                  map={map}
                  cal={cal}
                  battery={points.battery}
                  target={points.target}
                  weapon={weapon}
                  place={place}
                  onMove={move}
                  filter={groundFilter}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
                  {T.calculator.loading}
                </div>
              )}
            </div>

            {/* ── Denetimler ve çözüm ───────────────────────────────────── */}
            <div className="flex min-w-0 flex-col gap-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="font-display text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {T.calculator.weapon}
                  </span>
                  <select
                    value={weaponId}
                    onChange={e => setWeaponId(e.target.value)}
                    className={`mt-1 ${selectClass}`}
                  >
                    {WEAPONS.map(w => (
                      <option key={w.id} value={w.id} className="bg-[#16161a] font-normal">
                        {w.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="font-display text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {T.calculator.map}
                  </span>
                  <select
                    value={map?.name ?? ''}
                    onChange={e => setMapName(e.target.value)}
                    className={`mt-1 ${selectClass}`}
                  >
                    {(maps ?? []).map(m => (
                      <option key={m.name} value={m.name} className="bg-[#16161a] font-normal">
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <p className="text-[11px] leading-relaxed text-muted-foreground">
                {T.calculator.placeHint}
              </p>

              {pointRow('battery')}
              {pointRow('target')}

              {/* Atış çözümü — sayfanın cevabı. */}
              <div className="rounded-sm border border-primary/30 bg-primary/5 p-3">
                <p className="font-display text-[10px] font-bold uppercase tracking-wider text-primary">
                  {T.calculator.elevation}
                </p>
                {elevation?.rows.length ? (
                  <div className="mt-1.5 flex flex-wrap items-end gap-x-6 gap-y-2">
                    {elevation.rows.map(row => (
                      <div key={row.label ?? 'single'}>
                        <p className="font-mono text-3xl font-bold leading-none text-foreground">
                          {row.value}
                          <span className="ml-1.5 font-display text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                            {T.calculator.mil}
                          </span>
                        </p>
                        {row.label && (
                          <p className="mt-1 font-display text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                            {row.label}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1.5 font-mono text-3xl font-bold leading-none text-muted-foreground/60">
                    -
                  </p>
                )}
                {elevation?.note && (
                  <p className="mt-2 text-[11px] leading-relaxed text-[#d86666]">{elevation.note}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                {stat(
                  T.calculator.distance,
                  solution ? `${Math.round(solution.distance)} m` : '-',
                )}
                {stat(
                  T.calculator.azimuth,
                  solution ? `${solution.azimuth.toFixed(1)}°` : '-',
                )}
                {stat(
                  T.calculator.range,
                  `${weapon.minRangeM}–${weapon.maxRangeM} m`,
                  solution?.inRange ? 'good' : 'bad',
                )}
                {stat(
                  T.calculator.offset,
                  solution
                    ? `${solution.east >= 0 ? '+' : '−'}${Math.abs(Math.round(solution.east))} / ` +
                      `${solution.north >= 0 ? '+' : '−'}${Math.abs(Math.round(solution.north))}`
                    : '-',
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Zemin iki parçalı bir seçici, bir aç/kapa düğmesi değil:
                    tek düğme üzerinde yazan şeyin mevcut mod mu yoksa
                    geçilecek mod mu olduğu hiçbir zaman belli olmaz. */}
                <div className="flex flex-1 overflow-hidden rounded-sm border border-white/12">
                  {(['tactical', 'satellite'] as const).map(mode => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setViewMode(mode)}
                      aria-pressed={viewMode === mode}
                      className={`flex-1 px-2 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        viewMode === mode
                          ? 'bg-white/15 text-foreground'
                          : 'bg-white/5 text-foreground/55 hover:bg-white/10 hover:text-foreground'
                      }`}
                    >
                      {mode === 'tactical' ? T.calculator.viewTactical : T.calculator.viewSatellite}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={reset}
                  className="flex items-center justify-center gap-1.5 rounded-sm border border-white/12 bg-white/5 px-3 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider text-foreground/80 transition-colors hover:bg-white/10 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {T.calculator.reset}
                </button>
              </div>

              {/* Tablolar bizim ölçümümüz değil: kaynağı yazmak hem doğrusu,
                  hem de bir tablo yanlış geldiğinde nereye bakılacağını söyler. */}
              <p className="text-[11px] leading-relaxed text-muted-foreground/70">
                {T.calculator.credit}{' '}
                <a
                  href="https://github.com/apollyon-sys/wardogs-calculator"
                  target="_blank"
                  rel="noreferrer"
                  className="text-foreground/70 underline decoration-white/25 underline-offset-2 transition-colors hover:text-foreground"
                >
                  wardogs-calculator
                </a>
                {T.calculator.creditTail}
              </p>
            </div>
          </div>
        )}
      </section>
    </HomeShell>
  )
}
